import React, { useState } from "react";
import { Card, Button, Table, Typography, Row, Col, Tag, Popover } from "antd";
import { DownOutlined, RightOutlined, WarningFilled } from "@ant-design/icons";
import { IDeleteRequestState } from "../../pages/Board/types";
import ReConfirmModal from "components/ReConfirmModal";
import "./style.scss";

const { Title, Text } = Typography;

/**
 * DeleteConfirmation component displays details of a deletion request and allows the user to approve or reject it.
 *
 * Features:
 * - Shows space, domain, reason, and creation time for the deletion request.
 * - Allows toggling the display of file details.
 * - Provides Approve and Reject buttons, which are enabled only if the user has access.
 *
 * @component
 * @param {Object} props
 * @param {IDeleteRequest} props.request - The deletion request data to display.
 * @param {() => void} props.onApprove - Callback when the Approve button is clicked.
 * @param {() => void} props.onReject - Callback when the Reject button is clicked.
 * @param {boolean} [props.haveAccess=false] - Whether the current user has permission to approve/reject.
 */
const DeleteConfirmation: React.FC<{ request: IDeleteRequestState; onApprove: () => void; onReject: () => void; haveAccess: boolean }> = ({ request, onApprove, onReject, haveAccess = false }) => {
  // Auto-expand file list for delete marker so the badge is immediately visible
  const [showFiles, setShowFiles] = useState(request?.is_delete_marker === "true");
  const [rcmodal, setrcmodal] = useState(false);
  const [rcState, setrcState] = useState("");

  const handleApprove = () => {
    setrcmodal(true);
    setrcState("Approve");
  };

  const handleReject = () => {
    setrcmodal(true);
    setrcState("Reject");
  };

  const handleAccept = () => {
    setrcmodal(false);
    if (rcState == "Approve") {
      onApprove();
    } else if (rcState == "Reject") {
      onReject();
    }
  };

  const handleCancel = () => {
    setrcmodal(false);
  };

  const isDeleteMarker = request?.is_delete_marker === "true";

  const deleteMarkerPopoverContent = (
    <div className="delete-marker-popover__content">
      <p>
        You are approving the <strong>removal of an S3 delete marker</strong>, not
        the deletion of file content. Approving this will{" "}
        <strong>restore (undelete)</strong> the file in the GxP space.
      </p>
      {request.version_id && (
        <p>
          <strong>Delete Marker Version ID:</strong>{" "}
          <code>{request.version_id}</code>
        </p>
      )}
    </div>
  );

  const deleteMarkerBadge = isDeleteMarker ? (
    <Popover
      content={deleteMarkerPopoverContent}
      title={
        <span className="delete-marker-popover__title">
          <WarningFilled />
          Delete Marker Removal
        </span>
      }
      trigger="hover"
      placement="right"
      overlayStyle={{ maxWidth: 360 }}
    >
      <Tag
        color="gold"
        icon={<WarningFilled />}
        className="delete-marker-popover__tag"
      >
        Delete Marker
      </Tag>
    </Popover>
  ) : null;

  const cardTitle = <Title level={4} className="delete-confirmation__card-title">Delete Confirmation</Title>;

  const columns = [
    {
      title: "File Details",
      dataIndex: "file",
      key: "file",
      render: (file: string) => (
        <span className="delete-confirmation__file-cell">
          <span>{file}</span>
          {isDeleteMarker && deleteMarkerBadge}
        </span>
      ),
    },
  ];

  const dataSource = request?.selected_prefixes?.map((file, index) => ({
    key: index,
    file,
  }));

  return (
    <Card title={cardTitle} bordered={true}>
      <Row gutter={16} className="delete-confirmation__row">
        <Col xs={24} sm={8}>
          <Text strong>Space name:</Text> {request.space_name || "-"}
        </Col>
        <Col xs={24} sm={8}>
          <Text strong>Domain:</Text> {request.domain_name || "-"}
        </Col>
        <Col xs={24} sm={8}>
          <Text strong>Region:</Text> {request.region_name || "-"}
        </Col>
      </Row>
      <Row className="delete-confirmation__row">
        <Col xs={24}>
          <Text strong>Reason:</Text> {request.reason_for_deletion || "-"}
        </Col>
      </Row>
      <Row gutter={16} className="delete-confirmation__row">
        <Col xs={24} sm={12}>
          <Text strong>Created By:</Text> {request.loggedInUserEmail || "-"}
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>Created At:</Text> {request.deletionRequestedTime || "-"}
        </Col>
      </Row>
      <Row className="delete-confirmation__row">
        <Col xs={24}>
          <Text strong>Status:</Text> {request.status || "-"}
        </Col>
      </Row>
      <div className="delete-confirmation__actions">
        {request?.status?.toLocaleLowerCase() === "pending" && (
          <>
            <Button
              type="primary"
              onClick={handleApprove}
              className="delete-confirmation__approve-btn"
              disabled={!haveAccess}
            >
              Approve
            </Button>
            <Button onClick={handleReject} disabled={!haveAccess}>
              Reject
            </Button>
          </>
        )}
      </div>
      {Array.isArray(request?.selected_prefixes) && request.selected_prefixes.length > 0 && (
        <div className="delete-confirmation__file-toggle" onClick={() => setShowFiles(!showFiles)}>
          {showFiles ? <DownOutlined /> : <RightOutlined />}
          <Text strong className="delete-confirmation__file-toggle-label">
            File Details ({request?.selected_prefixes.length} files)
          </Text>
        </div>
      )}
      {showFiles && (
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          className="delete-confirmation__file-table"
          scroll={{ y: 200 }}
        />
      )}
      <ReConfirmModal
        useCase={rcState}
        visible={rcmodal}
        onCancel={handleCancel}
        onConfirm={handleAccept}
      />
    </Card>
  );
};

export default DeleteConfirmation;
